import type { IAttachmentRepository } from "@/repositories/interfaces/attachment.interface";
import type { IControlResultRepository } from "@/repositories/interfaces/control-result-repository.interface";
import type { IUserRepository } from "@/repositories/interfaces/user-repository.interface";
import type React from "react";
import { createContext, useContext, useMemo } from "react";
import type { ICompanyGroupRepository } from "../../repositories/interfaces/company-group-repository.interface";
import type { ICompanyRepository } from "../../repositories/interfaces/company-repository.interface";
import type { IControlRepository } from "../../repositories/interfaces/control-repository.interface";
import type { IRoleRepository } from "../../repositories/interfaces/role-repository.interface";
import type { RepositoryContainer } from "../../repositories/repository-container";

const RepositoryContext = createContext<RepositoryContainer | null>(null);

export interface RepositoryProviderProps {
  children: React.ReactNode;
  repositories: RepositoryContainer;
}

export function RepositoryProvider({
  children,
  repositories,
}: RepositoryProviderProps) {
  const value = useMemo(() => repositories, [repositories]);

  return (
    <RepositoryContext.Provider value={value}>
      {children}
    </RepositoryContext.Provider>
  );
}

// Base hook to access all repositories
export function useRepositories(): RepositoryContainer {
  const context = useContext(RepositoryContext);

  if (!context) {
    throw new Error(
      "useRepositories must be used within a RepositoryProvider. " +
        "Make sure your component tree is wrapped with <RepositoryProvider>.",
    );
  }

  return context;
}

// Convenience hooks for individual repositories
export function useCompanyGroupRepository(): ICompanyGroupRepository {
  return useRepositories().companyGroupRepository;
}

export function useCompanyRepository(): ICompanyRepository {
  return useRepositories().companyRepository;
}

export function useControlRepository(): IControlRepository {
  return useRepositories().controlRepository;
}

export function useRoleRepository(): IRoleRepository {
  return useRepositories().roleRepository;
}

export function useControlResultRepository(): IControlResultRepository {
  return useRepositories().controlResultRepository;
}

export function useUserRepository(): IUserRepository {
  return useRepositories().userRepository;
}

export function useUserGroupRepository() {
  return useRepositories().userGroupRepository;
}

export function useAttachmentRepository(): IAttachmentRepository {
  return useRepositories().attachmentRepository;
}

export function usePriorityRepository() {
  return useRepositories().priorityRepository;
}
